"use client";

import { useState, useEffect, useCallback } from "react";
import {
  X,
  Share2,
  Shield,
  Clock,
  MapPin,
  Award,
  Flag,
  ChevronRight,
  Loader2,
} from "lucide-react";
import {
  getPublicNews,
  getPublicNewsBySlug,
  NewsListItemPublic,
  NewsDetailPublic,
} from "@/lib/api";
import { useRouter } from "next/navigation";
import router from "next/router";

// ─── Constants ────────────────────────────────────────────────────────────────

const CATEGORIES = [
  "All News",
  "Partnership",
  "Feature",
  "Tourism",
  "Awards",
  "General",
];

const TAG_STYLES: Record<string, string> = {
  Partnership: "bg-purple-50 text-purple-700 border-purple-100",
  Feature: "bg-blue-50 text-blue-700 border-blue-100",
  Tourism: "bg-green-50 text-green-700 border-green-100",
  Awards: "bg-amber-50 text-amber-700 border-amber-100",
  General: "bg-gray-50 text-gray-700 border-gray-200",
};

// ─── Fallback icon ────────────────────────────────────────────────────────────

function CategoryIcon({
  category,
  size = 28,
}: {
  category: string;
  size?: number;
}) {
  const props = { size, className: "text-purple-200" };
  switch (category) {
    case "Partnership":
      return <Shield {...props} />;
    case "Feature":
      return <Clock {...props} />;
    case "Tourism":
      return <MapPin {...props} />;
    case "Awards":
      return <Award {...props} />;
    default:
      return <Flag {...props} />;
  }
}

// ─── Cover image — uses flat coverImageUrl from list API ─────────────────────

function ListCover({
  item,
  className = "w-full h-full object-cover",
}: {
  item: NewsListItemPublic;
  className?: string;
}) {
  if (item.coverImageUrl) {
    return (
      <img
        src={item.coverImageUrl}
        alt={item.coverImageAltText || item.title}
        className={className}
        onError={(e) => {
          (e.target as HTMLImageElement).style.display = "none";
        }}
      />
    );
  }
  return (
    <div className="w-full h-full flex items-center justify-center">
      <CategoryIcon category={item.category} size={28} />
    </div>
  );
}

// ─── Cover image — uses images[] from detail API ──────────────────────────────

function DetailCover({ article }: { article: NewsDetailPublic }) {
  const cover =
    article.images?.find((img) => img.isCover) ?? article.images?.[0];
  if (cover?.imageUrl) {
    return (
      <img
        src={cover.imageUrl}
        alt={cover.altText || article.title}
        className="w-full h-full object-cover"
        onError={(e) => {
          (e.target as HTMLImageElement).style.display = "none";
        }}
      />
    );
  }
  return (
    <div className="w-full h-full flex items-center justify-center">
      <CategoryIcon category={article.category} size={28} />
    </div>
  );
}

// ─── Format date ──────────────────────────────────────────────────────────────

function formatDate(dateStr: string) {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

// ─── Article Modal — fetches full detail by slug ──────────────────────────────

function ArticleModal({
  item,
  onClose,
}: {
  item: NewsListItemPublic;
  onClose: () => void;
}) {
  const [detail, setDetail] = useState<NewsDetailPublic | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(true);
  const [shareStatus, setShareStatus] = useState<"idle" | "copied" | "error">(
    "idle",
  );

  useEffect(() => {
    if (!item?.slug) return;
    setLoadingDetail(true);
    setDetail(null);
    getPublicNewsBySlug(item.slug).then((res) => {
      if (res.success && res.data) setDetail(res.data as NewsDetailPublic);
      setLoadingDetail(false);
    });
  }, [item?.slug]);

  const wordCount = detail?.content?.split(/\s+/).length ?? 0;
  const readTime = `${Math.max(1, Math.ceil(wordCount / 200))} min read`;

  const handleShare = async () => {
    const shareUrl = `${window.location.origin}/footer/company/news/${item.slug}`;
    const shareData = {
      title: item.title,
      text: item.summary,
      url: shareUrl,
    };

    try {
      if (navigator.share && navigator.canShare?.(shareData)) {
        // Native share sheet (mobile)
        await navigator.share(shareData);
      } else {
        // Fallback: copy to clipboard
        await navigator.clipboard.writeText(shareUrl);
        setShareStatus("copied");
        setTimeout(() => setShareStatus("idle"), 2000);
      }
    } catch (err) {
      // User cancelled native share — don't show error
      if (err instanceof Error && err.name !== "AbortError") {
        setShareStatus("error");
        setTimeout(() => setShareStatus("idle"), 2000);
      }
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/50 flex items-start justify-center p-4 sm:p-8 overflow-y-auto"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-white rounded-2xl w-full max-w-2xl border border-gray-100 shadow-xl overflow-hidden my-auto">
        {/* Header */}
        <div className="bg-gradient-to-br from-purple-800 to-purple-600 px-7 py-6 relative pt-16 md:pt-20">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-8 h-8 rounded-lg bg-white/20 hover:bg-white/30 flex items-center justify-center text-white transition-colors"
          >
            <X size={16} />
          </button>
          <span
            className={`inline-block text-xs font-semibold px-3 py-1 rounded-full border mb-3 ${TAG_STYLES[item.category] ?? TAG_STYLES.General}`}
          >
            {item.category}
          </span>
          <h2 className="text-xl sm:text-2xl font-bold text-white leading-tight mb-3">
            {item.title}
          </h2>
          <p className="text-purple-200 text-sm">
            {formatDate(item.publishedAt)} {detail && `· ${readTime}`}
          </p>
        </div>

        {/* Cover image */}
        <div className="w-full h-48 bg-gradient-to-br from-purple-50 to-purple-100 overflow-hidden border-b border-gray-100">
          {detail ? (
            <DetailCover article={detail} />
          ) : (
            <ListCover item={item} />
          )}
        </div>

        {/* Body */}
        <div className="px-7 py-6 space-y-4 min-h-[120px]">
          {loadingDetail ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 size={24} className="animate-spin text-purple-400" />
            </div>
          ) : detail ? (
            detail.content?.split("\n\n").map((para, i) => (
              <p
                key={i}
                className="text-gray-600 text-sm sm:text-base leading-relaxed"
              >
                {para}
              </p>
            ))
          ) : (
            <p className="text-gray-500 text-sm">{item.summary}</p>
          )}
        </div>

        {/* Footer */}
        <div className="px-7 py-4 border-t border-gray-100 flex items-center justify-between gap-3 flex-wrap">
          <button
            onClick={handleShare}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm transition-colors ${
              shareStatus === "copied"
                ? "border-green-200 bg-green-50 text-green-600"
                : shareStatus === "error"
                  ? "border-red-200 bg-red-50 text-red-600"
                  : "border-gray-200 text-gray-600 hover:bg-gray-50"
            }`}
          >
            <Share2 size={14} />
            {shareStatus === "copied"
              ? "Link copied!"
              : shareStatus === "error"
                ? "Failed to share"
                : "Share"}
          </button>
          <button
            onClick={onClose}
            className="flex items-center gap-2 px-5 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 text-white text-sm font-semibold transition-colors"
          >
            Close <X size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Cards ────────────────────────────────────────────────────────────────────

function FeaturedCard({
  article,
  onClick,
}: {
  article: NewsListItemPublic;
  onClick: () => void;
}) {
  const router = useRouter();
  return (
    <button
      onClick={() => router.push(`/footer/company/news/${article.slug}`)}
      className="group bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden text-left w-full hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
    >
      <div className="w-full h-52 bg-gradient-to-br from-purple-50 to-purple-100 overflow-hidden">
        <ListCover
          item={article}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        />
      </div>
      <div className="p-5">
        <div className="flex items-center gap-2 mb-2">
          <span
            className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${TAG_STYLES[article.category] ?? TAG_STYLES.General}`}
          >
            {article.category}
          </span>
          {article.isFeatured && (
            <span className="text-xs bg-amber-50 text-amber-700 border border-amber-100 font-semibold px-2.5 py-1 rounded-full">
              Featured
            </span>
          )}
        </div>
        <h3 className="text-base sm:text-lg font-bold text-gray-900 leading-snug mb-2">
          {article.title}
        </h3>
        <p className="text-sm text-gray-500 leading-relaxed mb-3 line-clamp-3">
          {article.summary}
        </p>
        <p className="text-xs text-gray-400">
          {formatDate(article.publishedAt)}
        </p>
      </div>
    </button>
  );
}

function SideCard({
  article,
  onClick,
}: {
  article: NewsListItemPublic;
  onClick: () => void;
}) {
  const router = useRouter();
  return (
    <button
      onClick={() => router.push(`/footer/company/news/${article.slug}`)}
      className="group bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden text-left w-full hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 flex"
    >
      <div className="w-24 sm:w-28 shrink-0 bg-gradient-to-br from-purple-50 to-purple-100 overflow-hidden">
        <ListCover
          item={article}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        />
      </div>
      <div className="p-4 flex flex-col justify-center min-w-0">
        <span
          className={`text-xs font-semibold px-2.5 py-0.5 rounded-full border inline-block w-fit mb-1.5 ${TAG_STYLES[article.category] ?? TAG_STYLES.General}`}
        >
          {article.category}
        </span>
        <h3 className="text-sm font-bold text-gray-900 leading-snug mb-1.5 line-clamp-2">
          {article.title}
        </h3>
        <p className="text-xs text-gray-400">
          {formatDate(article.publishedAt)}
        </p>
      </div>
    </button>
  );
}

function SmallCard({
  article,
  onClick,
}: {
  article: NewsListItemPublic;
  onClick: () => void;
}) {
  const router = useRouter();
  return (
    <button
      onClick={() => router.push(`/footer/company/news/${article.slug}`)}
      className="group bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden text-left w-full hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
    >
      <div className="p-4 flex gap-3 items-start">
        <div className="w-14 h-14 shrink-0 rounded-xl bg-purple-50 overflow-hidden">
          <ListCover item={article} className="w-full h-full object-cover" />
        </div>
        <div className="min-w-0 flex-1">
          <span
            className={`text-xs font-semibold px-2 py-0.5 rounded-full border inline-block mb-1.5 ${TAG_STYLES[article.category] ?? TAG_STYLES.General}`}
          >
            {article.category}
          </span>
          <h3 className="text-sm font-bold text-gray-900 leading-snug mb-1 line-clamp-2">
            {article.title}
          </h3>
          <p className="text-xs text-gray-400">
            {formatDate(article.publishedAt)}
          </p>
        </div>
        <ChevronRight
          size={16}
          className="text-gray-300 group-hover:text-purple-400 transition-colors shrink-0 mt-1"
        />
      </div>
    </button>
  );
}

// ─── Skeleton ─────────────────────────────────────────────────────────────────

function SkeletonCard() {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden animate-pulse">
      <div className="h-52 bg-gray-100" />
      <div className="p-5 space-y-3">
        <div className="h-4 bg-gray-100 rounded w-20" />
        <div className="h-5 bg-gray-100 rounded w-3/4" />
        <div className="h-4 bg-gray-100 rounded w-full" />
        <div className="h-4 bg-gray-100 rounded w-2/3" />
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

const PAGE_SIZE = 9;

export default function NewsPage() {
  const [activeCategory, setActiveCategory] = useState("All News");
  const [selectedArticle, setSelectedArticle] =
    useState<NewsListItemPublic | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [articles, setArticles] = useState<NewsListItemPublic[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchNews = useCallback(async (page: number, category: string) => {
    setLoading(true);
    setError(null);
    try {
      const res = await getPublicNews({
        page,
        pageSize: PAGE_SIZE,
        ...(category !== "All News" ? { category } : {}),
      });
      if (!res.success) throw new Error("Failed to load news");
      const data = res.data as import("@/lib/api").NewsListResponse;
      setArticles(data.items ?? []);
      setTotalCount(data.totalCount ?? 0);
    } catch (err: any) {
      setError(err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNews(currentPage, activeCategory);
  }, [currentPage, activeCategory, fetchNews]);

  const handleCategoryChange = (cat: string) => {
    setActiveCategory(cat);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);
  const featured = articles[0] ?? null;
  const sideArticles = articles.slice(1, 3);
  const gridArticles = articles.slice(3);

  return (
    <>
      <div className="min-h-screen bg-gray-50">
        {/* Hero */}
        <div className="bg-gradient-to-br from-purple-900 via-purple-700 to-purple-500 px-6 pt-24 md:pt-28 pb-14 sm:pb-20 text-center">
          {" "}
          <p className="text-purple-300 text-xs font-semibold uppercase tracking-widest mb-3">
            Lily Lanka
          </p>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white mb-3 tracking-tight">
            News & Updates
          </h1>
          <p className="text-purple-200 text-sm sm:text-base max-w-md mx-auto mb-8">
            Stay updated with the latest from Sri Lanka's leading travel
            platform
          </p>
          <div className="flex flex-wrap gap-2 justify-center">
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                onClick={() => handleCategoryChange(cat)}
                className={`px-4 py-2 rounded-full text-xs font-semibold border transition-all ${
                  activeCategory === cat
                    ? "bg-white text-purple-700 border-white shadow-sm"
                    : "bg-transparent text-purple-200 border-purple-400/40 hover:border-white/60 hover:text-white"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-8">
          {/* Error */}
          {error && (
            <div className="text-center py-12">
              <p className="text-red-500 font-semibold mb-2">
                Failed to load news
              </p>
              <button
                onClick={() => fetchNews(currentPage, activeCategory)}
                className="text-sm text-purple-600 underline"
              >
                Try again
              </button>
            </div>
          )}

          {/* Loading */}
          {loading && !error && (
            <div className="space-y-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                <SkeletonCard />
                <div className="flex flex-col gap-4">
                  <div className="bg-white rounded-2xl border border-gray-100 h-28 animate-pulse" />
                  <div className="bg-white rounded-2xl border border-gray-100 h-28 animate-pulse" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {[1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className="bg-white rounded-2xl border border-gray-100 h-24 animate-pulse"
                  />
                ))}
              </div>
            </div>
          )}

          {/* Empty */}
          {!loading && !error && articles.length === 0 && (
            <div className="text-center py-20 text-gray-400">
              <p className="text-lg font-semibold text-gray-500 mb-1">
                No articles found
              </p>
              <p className="text-sm">Try selecting a different category</p>
            </div>
          )}

          {/* Content */}
          {!loading && !error && articles.length > 0 && (
            <>
              {featured && (
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <p className="text-xs font-bold text-purple-600 uppercase tracking-widest">
                      Featured
                    </p>
                    <div className="flex-1 h-px bg-gray-200" />
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                    <FeaturedCard
                      article={featured}
                      onClick={() =>
                        router.push(`/footer/company/news/${featured.slug}`)
                      }
                    />
                    {sideArticles.length > 0 && (
                      <div className="flex flex-col gap-4">
                        {sideArticles.map((a) => (
                          <SideCard
                            key={a.uid}
                            article={a}
                            onClick={() => setSelectedArticle(a)}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {gridArticles.length > 0 && (
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <p className="text-xs font-bold text-purple-600 uppercase tracking-widest">
                      Latest updates
                    </p>
                    <div className="flex-1 h-px bg-gray-200" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {gridArticles.map((a) => (
                      <SmallCard
                        key={a.uid}
                        article={a}
                        onClick={() => setSelectedArticle(a)}
                      />
                    ))}
                  </div>
                </div>
              )}

              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 pt-2">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="w-9 h-9 rounded-xl border border-gray-200 bg-white flex items-center justify-center text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    ‹
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                    (p) => (
                      <button
                        key={p}
                        onClick={() => handlePageChange(p)}
                        className={`w-9 h-9 rounded-xl text-sm font-semibold transition-colors ${
                          p === currentPage
                            ? "bg-purple-600 text-white shadow-sm shadow-purple-200"
                            : "border border-gray-200 bg-white text-gray-600 hover:bg-gray-50"
                        }`}
                      >
                        {p}
                      </button>
                    ),
                  )}
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="w-9 h-9 rounded-xl border border-gray-200 bg-white flex items-center justify-center text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    ›
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {selectedArticle && (
        <ArticleModal
          item={selectedArticle}
          onClose={() => setSelectedArticle(null)}
        />
      )}
    </>
  );
}
